<template>
    <div>
        <LsInputLabel :label="label" :error="getValidation.invalid" />
        <BField :type="`${getValidation.invalid ? 'is-error' : 'is-secondary-light'}`">
            <BInput
                :id="id"
                ref="autocomplete"
                v-model="autocompleteText"
                v-validate="validation"
                custom-class="ls-google-autocomplete--custom"
                :placeholder="placeholder"
                test-id="ls-google-autocomplete"
                :name="name"
                @focus="onFocus"
                @change.native="onChange"
            />
        </BField>
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import { lsAddressHelpers, lsValidationService } from 'ls-vue-common';
import _ from 'lodash';
import LsInputLabel from '@/components/common/LsInputLabel/LsInputLabel.vue';

export default {
    name: 'LsGoogleAutocomplete',
    inject: ['$validator'],
    components: { LsInputLabel },
    props: {
        id: VueTypes.string.def('autocomplete'),
        placeholder: VueTypes.string.def('e.g. 123 Main st'),
        label: VueTypes.string.def('Street address'),
        error: VueTypes.bool.def(false),
        name: VueTypes.string.isRequired,
        enableGeolocation: VueTypes.bool.def(true),
        validation: VueTypes.object.def(_.constant(null)),
    },
    data() {
        return {
            /**
             * The Autocomplete object.
             *
             * @type {Autocomplete}
             * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
             */
            autocomplete: null,

            /**
             * Autocomplete input text
             * @type {String}
             */
            autocompleteText: '',

            geolocation: {
                /**
                 * Google Geocoder Objet
                 * @type {Geocoder}
                 * @link https://developers.google.com/maps/documentation/javascript/reference#Geocoder
                 */
                geocoder: null,

                /**
                 * Filled after geolocate result
                 * @type {Coordinates}
                 * @link https://developer.mozilla.org/en-US/docs/Web/API/Coordinates
                 */
                loc: null,

                /**
                 * Filled after geolocate result
                 * @type {Position}
                 * @link https://developer.mozilla.org/en-US/docs/Web/API/Position
                 */
                position: null,
            },
            input: null,
        };
    },
    computed: {
        getValidation() {
            return lsValidationService.getValidation.call(this);
        },
    },
    watch: {
        autocompleteText(newVal, oldVal) {
            this.$emit('inputChange', { newVal, oldVal }, this.id);
        },
    },
    mounted() {
        const options = {
            types: ['address'],
            componentRestrictions: {
                country: 'us',
            },
        };

        this.input = document.getElementById(this.id);

        // The Places script loads from index.html. If it failed, tell the parent instead of throwing.
        const Autocomplete = _.get(window, 'google.maps.places.Autocomplete');
        if (!_.isFunction(Autocomplete)) {
            this.$emit('places-unavailable');
            return;
        }

        try {
            this.autocomplete = new Autocomplete(this.input, options);
            this.autocomplete.addListener('place_changed', this.onPlaceChanged);
        } catch (err) {
            this.autocomplete = null;
            this.$emit('places-unavailable', err);
        }
    },
    methods: {
        /**
         * When a place changed
         */
        onPlaceChanged() {
            const place = this.autocomplete.getPlace();

            if (place)
                if (!place.geometry) {
                    // User entered the name of a Place that was not suggested and
                    // pressed the Enter key, or the Place Details request failed.
                    this.$emit('no-results-found', place, this.id);
                    return;
                }

            if (place.address_components !== undefined) {
                // return returnData object and PlaceResult object
                const result = lsAddressHelpers.formatPlace({ place });
                // assign just the street value to input
                this.autocompleteText = result.street;
                // prevent input from showing full address when selecting same prediction
                this.input.value = this.autocompleteText;
                // emit change event
                this.$emit('placechanged', result);

                this.onChange();
            }
        },

        /**
         * When the input gets focus
         */
        onFocus() {
            this.biasAutocompleteLocation();
            this.$emit('focus');
        },

        /**
         * When the input got changed
         */
        onChange() {
            this.$emit('change', this.autocompleteText);
        },

        /**
         * Clear the input
         */
        clear() {
            this.autocompleteText = '';
        },

        /**
         * Focus the input
         */
        focus() {
            this.$refs.autocomplete.focus();
        },

        /**
         * Update internal location from navigator geolocation
         * @param  {Function} (geolocation, position)
         */
        updateGeolocation(callback = null) {
            if (navigator.geolocation) {
                let options = {};
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        let geolocation = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        };
                        this.geolocation.loc = geolocation;
                        this.geolocation.position = position;

                        if (callback) callback(geolocation, position);
                    },
                    (err) => {
                        this.$emit('error', 'Cannot get Coordinates from navigator', err);
                    },
                    options
                );
            }
        },

        // Bias the autocomplete object to the user's geographical location,
        // as supplied by the browser's 'navigator.geolocation' object.
        biasAutocompleteLocation() {
            if (this.enableGeolocation && this.autocomplete) {
                this.updateGeolocation((geolocation, position) => {
                    // eslint-disable-next-line no-undef
                    let circle = new google.maps.Circle({
                        center: geolocation,
                        radius: position.coords.accuracy,
                    });
                    this.autocomplete.setBounds(circle.getBounds());
                });
            }
        },
    },
};
</script>

<style lang="scss">
.ls-google-autocomplete--custom {
    height: 36px;
}
</style>
